import React, { useState } from 'react';
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../hooks/useAuth';
import AppointmentDialog from './AppointmentDialog';

// "Dar turno" desde fuera de la Agenda (perfil del paciente, lista de
// Pacientes) — pedido explícito del usuario 2026-10-13. AppointmentDialog
// necesita doctorId ya elegido y un start_time de partida ANTES de
// abrirse (no tiene selector de médico propio adentro) — este mini-form
// pide solo médico + fecha, sin hora exacta: una vez abierto el diálogo
// completo, el usuario ya puede ver los horarios libres de ese día y
// elegir ahí (mismo flujo que ya existe en Agenda, ver
// AppointmentDialog.js::showSlots). Mismo permiso que crear turnos en el
// resto de la app (admin/recepcionista sin restricción, médico sobre
// cualquier agenda — ver CalendarGrid.js).
//
// Un solo componente para los puntos de entrada fuera de la Agenda (perfil
// del paciente: inline junto a la tabla de turnos; lista de Pacientes:
// dentro de un Dialog chico lanzado desde el menú de fila; /turnos: sin
// paciente fijo, con el buscador propio del AppointmentDialog) — evita
// mantener el mismo mini-form + orquestación del AppointmentDialog
// duplicados en varios lugares.
// patient es opcional: si no viene (caso /turnos), el diálogo completo
// arranca sin paciente precargado y el usuario lo busca ahí mismo con el
// Autocomplete que el diálogo ya tiene — mismo comportamiento que crear un
// turno desde un hueco vacío de la Agenda.
// onContinue es opcional: cuando el consumidor ya tiene su propio
// AppointmentDialog montado (ej. AppointmentsTab.js, que lo reusa también
// para editar turnos existentes al click en una fila), le pasa el
// dialogEvent armado acá y maneja la apertura él mismo, en vez de que el
// hook monte un segundo AppointmentDialog por su cuenta.
export const useNewAppointmentFlow = ({ patient, doctorsArr = [], doctorsMap: doctorsMapProp, onSaved, onContinue }) => {
  const { isAdmin, isRecepcionista, isMedico, doctorId: myDoctorId } = useAuth();
  const canCreateAppointment = isAdmin || isRecepcionista || isMedico;
  const [showForm, setShowForm] = useState(false);
  const [doctorIdValue, setDoctorIdValue] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEvent, setDialogEvent] = useState(null);

  const doctorsMap = doctorsMapProp || Object.fromEntries(doctorsArr.map(d => [d.id, d]));

  const openForm = () => {
    setDoctorIdValue(isMedico && !isAdmin && !isRecepcionista && myDoctorId ? myDoctorId : '');
    setDateValue(new Date().toISOString().slice(0, 10));
    setShowForm(true);
  };

  const closeForm = () => setShowForm(false);

  const handleContinue = () => {
    if (!doctorIdValue || !dateValue) return;
    // Hora de partida arbitraria (10:00) — el usuario la ajusta en el
    // diálogo completo, ya sea a mano o viendo horarios libres ahí mismo.
    const event = { doctor_id: Number(doctorIdValue), patient_id: patient?.id, start_time: `${dateValue}T10:00:00` };
    setShowForm(false);
    if (onContinue) {
      onContinue(event);
    } else {
      setDialogEvent(event);
      setDialogOpen(true);
    }
  };

  const FormFields = () => (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
        <InputLabel>Médico *</InputLabel>
        <Select
          value={doctorIdValue}
          label="Médico *"
          onChange={e => setDoctorIdValue(e.target.value)}
          disabled={isMedico && !isAdmin && !isRecepcionista}
        >
          {doctorsArr.map(d => <MenuItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField
        label="Fecha *" type="date" size="small" value={dateValue}
        onChange={e => setDateValue(e.target.value)}
        InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
      />
    </Box>
  );

  const TriggerButton = (props) => (
    canCreateAppointment ? (
      <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />} onClick={openForm} {...props}>
        Nuevo turno
      </Button>
    ) : null
  );

  const Dialog_ = (!onContinue && dialogOpen) ? (
    <AppointmentDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      isEdit={false}
      selectedEvent={dialogEvent}
      doctorId={dialogEvent?.doctor_id}
      doctor={doctorsMap[dialogEvent?.doctor_id]}
      patients={patient ? [patient] : []}
      setPatients={() => {}}
      onSaved={() => { setDialogOpen(false); onSaved?.(); }}
    />
  ) : null;

  return {
    canCreateAppointment,
    showForm,
    openForm,
    closeForm,
    handleContinue,
    canContinue: !!doctorIdValue && !!dateValue,
    FormFields,
    TriggerButton,
    AppointmentDialogNode: Dialog_,
  };
};
